"use client"
import {
    MaterialReactTable,
    useMaterialReactTable,
} from 'material-react-table';
import {Box, Button, IconButton, Tooltip, useTheme} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { mkConfig, generateCsv, download } from "export-to-csv";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import FileDownloadIcon from "@mui/icons-material/FileDownload";


// CSV config
const csvConfig = mkConfig({
    fieldSeparator: ",",
    decimalSeparator: ".",
    useKeysAsHeaders: true,
});

export default function MRTDatatable({ columns, data, onEdit, onDelete}) {
    const theme = useTheme();

    const handleExportRowsCSV = (rows) => {
        const rowData = rows.map((row) => row.original);
        const csv = generateCsv(csvConfig)(rowData);
        download(csvConfig)(csv);
    };

    const handleExportAllCSV = () => {
        const csv = generateCsv(csvConfig)(data);
        download(csvConfig)(csv);
    };

    const handleExportExcel = (rows) => {
        const rowData = rows.map((row) => row.original);
        const worksheet = XLSX.utils.json_to_sheet(rowData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
        XLSX.writeFile(workbook, "table-data.xlsx");
    };

    const handleExportPDF = (rows) => {
        const rowData = rows.map((row) => row.original);
        const doc = new jsPDF();
        doc.autoTable({
            head: [columns.map((col) => col.header)],
            body: rowData.map((item) => columns.map((col) => item[col.accessorKey])),
        });
        doc.save("table-data.pdf");
    };

    const table = useMaterialReactTable({
        // ===== BASIC CONFIGURATION =====
        columns,
        data,
        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize: 50,
            },
            density: 'compact',
        },

        // ===== COLUMN FEATURES =====
        enableColumnActions: false, // Show column action buttons
        enableColumnFilters: true, // Enable column filtering
        enableColumnOrdering: false, // Enable column reordering
        enableColumnResizing: false, // Enable column resizing
        enableColumnVirtualization: false, // Enable column virtualization
        // enableHiding: true, // Enable column hiding
        enablePinning: false, // Enable column pinning
        enableSorting: true, // Enable column sorting
        enableSortingRemoval: true, // Allow removing sort
        enableMultiSort: false, // Enable multi-column sorting,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        // enableGlobalFilter: true, // Enable global search
        // enableGlobalFilterModes: true, // Enable global filter modes
        // globalFilterFn: 'fuzzy', // Global filter function
        // globalFilterMode: 'fuzzy', // Global filter mode

        // ===== ROW FEATURES =====
        // enableRowSelection: false, // Enable row selection
        // enableMultiRowSelection: true, // Enable multi-row selection
        // enableSubRowSelection: false, // Enable sub-row selection
        // enableRowNumbers: false, // Show row numbers
        // enableRowVirtualization: false, // Enable row virtualization
        // enableRowPinning: false, // Enable row pinning
        // enableRowDragging: false, // Enable row dragging
        // enableRowExpanding: false, // Enable row expanding
        // enableRowEditing: false, // Enable row editing
        // enableRowDeleting: false, // Enable row deleting
        // enableRowCreating: false, // Enable row creating

        // ===== PAGINATION =====
        enablePagination: true, // Enable pagination
        enableBottomToolbar: true, // Show bottom toolbar
        enableTopToolbar: true, // Show top toolbar
        paginationDisplayMode: 'default', // Pagination display mode
        positionPagination: 'bottom', // Pagination position
        muiPaginationProps: {
            rowsPerPageOptions: [50, 100, 250, 500],
        },


        // ===== DENSITY =====
        // enableDensityToggle: true, // Enable density toggle
        density: 'compact', // Table density: 'compact', 'comfortable', 'spacious'

        // ===== FULL SCREEN =====
        // enableFullScreenToggle: true, // Enable full screen toggle

        // ===== EXPORT =====
        // enableExport: true, // Enable export functionality
        // enableExportAll: true, // Enable export all data
        // enableExportData: true, // Enable export current data
        // enableExportColumns: true, // Enable export columns
        // exportAllData: true, // Export all data or just current page

        // ===== STYLING - MUI PROPS (Theme-Aware) =====
        muiTableProps: {
            sx: {
                backgroundColor: 'transparent',
                '& .MuiTableCell-root': {
                    borderColor: 'var(--template-palette-divider)',
                    color: 'var(--template-palette-text-primary)',
                    backgroundColor: 'transparent',
                },
                '& .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root': {
                    backgroundColor: 'transparent !important',
                    color: 'var(--template-palette-text-primary) !important',
                },
                '& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root': {
                    backgroundColor: 'var(--template-palette-action-hover) !important',
                }
            }
        },
        muiTableHeadProps: {
            sx: {
                '& .MuiTableCell-root': {
                    backgroundColor: 'var(--template-palette-background-level1)',
                    color: 'var(--template-palette-text-primary)',
                    fontWeight: 600,
                }
            }
        },
        muiTableBodyProps: {
            sx: {
                backgroundColor: 'transparent',
                '& .MuiTableRow-root': {
                    backgroundColor: 'transparent !important',
                    '& .MuiTableCell-root': {
                        backgroundColor: 'transparent !important',
                        color: 'var(--template-palette-text-primary) !important',
                        borderColor: 'var(--template-palette-divider)',
                    }
                },
                '& .MuiTableRow-root:hover': {
                    backgroundColor: 'var(--template-palette-action-hover) !important',
                    '& .MuiTableCell-root': {
                        backgroundColor: 'var(--template-palette-action-hover) !important',
                    }
                }
            }
        },
        muiTableContainerProps: {
            sx: {
                backgroundColor: 'transparent',
                '& .MuiTable-root': {
                    backgroundColor: 'transparent',
                },
                '& .MuiTableBody-root': {
                    backgroundColor: 'transparent',
                },
                '& .MuiTableRow-root': {
                    backgroundColor: 'transparent',
                },
                '& .MuiTableCell-root': {
                    backgroundColor: 'transparent',
                },
            }
        },
        muiTablePaperProps: {
            sx: {
                backgroundColor: 'transparent',
                color: 'var(--template-palette-text-primary)',
                boxShadow: 'none',
                '& .MuiTable-root': {
                    backgroundColor: 'transparent',
                },
                '& .MuiTableBody-root': {
                    backgroundColor: 'transparent',
                },
                '& .MuiTableRow-root': {
                    backgroundColor: 'transparent',
                },
                '& .MuiTableCell-root': {
                    backgroundColor: 'transparent',
                },
            }
        },
        muiTableHeadCellProps: {
            sx: {
                backgroundColor: 'var(--template-palette-background-level1)',
                color: 'var(--template-palette-text-primary)',
                borderColor: 'var(--template-palette-divider)',
            }
        },
        muiTableBodyCellProps: {
            sx: {
                backgroundColor: 'transparent !important',
                color: 'var(--template-palette-text-primary) !important',
                borderColor: 'var(--template-palette-divider)',
                '&:hover': {
                    backgroundColor: 'var(--template-palette-action-hover) !important',
                }
            }
        },
        muiTableRowProps: {
            sx: {
                backgroundColor: 'transparent !important',
                '& .MuiTableCell-root': {
                    backgroundColor: 'transparent !important',
                    color: 'var(--template-palette-text-primary) !important',
                },
                '&:hover': {
                    backgroundColor: 'var(--template-palette-action-hover) !important',
                    '& .MuiTableCell-root': {
                        backgroundColor: 'var(--template-palette-action-hover) !important',
                    }
                }
            }
        },
        muiToolbarProps: {
            sx: {
                backgroundColor: 'transparent',
                borderBottom: '1px solid var(--template-palette-divider)',
                color: 'var(--template-palette-text-primary)',
            }
        },
        muiTopToolbarProps: {
            sx: {
                backgroundColor: 'transparent',
                borderBottom: '1px solid var(--template-palette-divider)',
                color: 'var(--template-palette-text-primary)',
            }
        },
        muiBottomToolbarProps: {
            sx: {
                backgroundColor: 'transparent',
                borderTop: '1px solid var(--template-palette-divider)',
                color: 'var(--template-palette-text-primary)',
            }
        },
        muiSearchTextFieldProps: {
            sx: {
                '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--template-palette-background-paper)',
                    color: 'var(--template-palette-text-primary)',
                    '& fieldset': {
                        borderColor: 'var(--template-palette-divider)',
                    },
                    '&:hover fieldset': {
                        borderColor: 'var(--template-palette-primary-main)',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: 'var(--template-palette-primary-main)',
                    },
                },
                '& .MuiInputLabel-root': {
                    color: 'var(--template-palette-text-secondary)',
                },
            }
        },
        muiFilterTextFieldProps: {
            sx: {
                '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--template-palette-background-paper)',
                    color: 'var(--template-palette-text-primary)',
                    '& fieldset': {
                        borderColor: 'var(--template-palette-divider)',
                    },
                    '&:hover fieldset': {
                        borderColor: 'var(--template-palette-primary-main)',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: 'var(--template-palette-primary-main)',
                    },
                },
                '& .MuiInputLabel-root': {
                    color: 'var(--template-palette-text-secondary)',
                },
            }
        },
        muiSelectProps: {
            sx: {
                backgroundColor: 'var(--template-palette-background-paper)',
                color: 'var(--template-palette-text-primary)',
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--template-palette-divider)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--template-palette-primary-main)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--template-palette-primary-main)',
                },
            }
        },
        muiSwitchProps: {
            sx: {
                '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'var(--template-palette-primary-main)',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'var(--template-palette-primary-main)',
                },
            }
        },
        muiCheckboxProps: {
            sx: {
                color: 'var(--template-palette-text-secondary)',
                '&.Mui-checked': {
                    color: 'var(--template-palette-primary-main)',
                },
            }
        },
        muiRadioGroupProps: {
            sx: {
                color: 'var(--template-palette-text-primary)',
            }
        },
        muiSliderProps: {
            sx: {
                color: 'var(--template-palette-primary-main)',
            }
        },
        muiAutocompleteProps: {
            sx: {
                '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--template-palette-background-paper)',
                    color: 'var(--template-palette-text-primary)',
                    '& fieldset': {
                        borderColor: 'var(--template-palette-divider)',
                    },
                    '&:hover fieldset': {
                        borderColor: 'var(--template-palette-primary-main)',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: 'var(--template-palette-primary-main)',
                    },
                },
            }
        },
        muiDatePickerProps: {
            sx: {
                '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--template-palette-background-paper)',
                    color: 'var(--template-palette-text-primary)',
                    '& fieldset': {
                        borderColor: 'var(--template-palette-divider)',
                    },
                    '&:hover fieldset': {
                        borderColor: 'var(--template-palette-primary-main)',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: 'var(--template-palette-primary-main)',
                    },
                },
            }
        },
        muiTimePickerProps: {
            sx: {
                '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--template-palette-background-paper)',
                    color: 'var(--template-palette-text-primary)',
                    '& fieldset': {
                        borderColor: 'var(--template-palette-divider)',
                    },
                    '&:hover fieldset': {
                        borderColor: 'var(--template-palette-primary-main)',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: 'var(--template-palette-primary-main)',
                    },
                },
            }
        },
        muiDateTimePickerProps: {
            sx: {
                '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--template-palette-background-paper)',
                    color: 'var(--template-palette-text-primary)',
                    '& fieldset': {
                        borderColor: 'var(--template-palette-divider)',
                    },
                    '&:hover fieldset': {
                        borderColor: 'var(--template-palette-primary-main)',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: 'var(--template-palette-primary-main)',
                    },
                },
            }
        },

        // ===== LAYOUT =====
        layoutMode: 'semantic', // Layout mode: 'semantic', 'grid'
        positionToolbarAlertBanner: 'top', // Toolbar alert banner position
        positionToolbarDropZone: 'top', // Toolbar drop zone position

        // ===== DATA MANAGEMENT =====
        // getRowId: (row) => row.id, // Function to get row ID
        // getSubRows: (row) => row.subRows, // Function to get sub rows
        // getRowCanExpand: (row) => !!row.subRows, // Function to check if row can expand
        // getRowIsSelectable: (row) => true, // Function to check if row is selectable
        // getRowIsDeletable: (row) => true, // Function to check if row is deletable
        // getRowIsEditable: (row) => true, // Function to check if row is editable

        // ===== FILTERING =====
        // filterFns: {}, // Custom filter functions
        // globalFilterFn: 'fuzzy', // Global filter function
        // columnFilterFns: {}, // Column-specific filter functions
        // filterFromLeafRows: false, // Filter from leaf rows
        // maxLeafRowFilterDepth: 0, // Max leaf row filter depth

        // ===== SORTING =====
        // sortingFns: {}, // Custom sorting functions
        // sortDescFirst: false, // Sort descending first
        // enableSortingRemoval: true, // Enable sorting removal
        // isMultiSortEvent: (e) => e.shiftKey, // Multi-sort event handler

        // ===== GROUPING =====
        // enableGrouping: false, // Enable grouping
        // enableGroupingRemoval: true, // Enable grouping removal
        // groupingFns: {}, // Custom grouping functions
        // getGroupingValue: (row, columnId) => row[columnId], // Get grouping value

        // ===== EXPANDING =====
        // enableExpanding: false, // Enable expanding
        // enableSubRowSelection: false, // Enable sub-row selection
        // getSubRows: (row) => row.subRows, // Get sub rows
        // getRowCanExpand: (row) => !!row.subRows, // Check if row can expand
        // getExpandedRowModel: (table) => table.getExpandedRowModel(), // Get expanded row model

        // ===== COLUMN VISIBILITY =====
        // columnVisibility: {}, // Column visibility state
        // onColumnVisibilityChange: (updater) => {}, // Column visibility change handler
        // enableHiding: true, // Enable column hiding

        // ===== COLUMN ORDERING =====
        // columnOrder: [], // Column order
        // onColumnOrderChange: (updater) => {}, // Column order change handler
        // enableColumnOrdering: false, // Enable column ordering

        // ===== COLUMN SIZING =====
        // columnSizing: {}, // Column sizing state
        // onColumnSizingChange: (updater) => {}, // Column sizing change handler
        // enableColumnResizing: false, // Enable column resizing
        // columnResizeMode: 'onChange', // Column resize mode
        // enableResizing: false, // Enable resizing

        // ===== COLUMN PINNING =====
        // columnPinning: {}, // Column pinning state
        // onColumnPinningChange: (updater) => {}, // Column pinning change handler
        // enablePinning: false, // Enable column pinning

        // ===== ROW SELECTION =====
        // rowSelection: {}, // Row selection state
        // onRowSelectionChange: (updater) => {}, // Row selection change handler
        // enableRowSelection: false, // Enable row selection
        // enableMultiRowSelection: true, // Enable multi-row selection
        // enableSubRowSelection: false, // Enable sub-row selection
        // getRowId: (row) => row.id, // Get row ID
        // isRowSelectable: (row) => true, // Check if row is selectable

        // ===== ROW PINNING =====
        // rowPinning: {}, // Row pinning state
        // onRowPinningChange: (updater) => {}, // Row pinning change handler
        // enableRowPinning: false, // Enable row pinning

        // ===== PAGINATION STATE =====
        // pagination: {}, // Pagination state
        // onPaginationChange: (updater) => {}, // Pagination change handler
        // pageCount: -1, // Total page count
        // pageSize: 10, // Page size
        // pageIndex: 0, // Current page index
        // pageSizeOptions: [50, 100, 250, 500], // Page size options

        // ===== GLOBAL FILTER STATE =====
        // globalFilter: '', // Global filter value
        // onGlobalFilterChange: (updater) => {}, // Global filter change handler
        // enableGlobalFilter: true, // Enable global filter
        // globalFilterFn: 'fuzzy', // Global filter function
        // globalFilterMode: 'fuzzy', // Global filter mode

        // ===== SORTING STATE =====
        // sorting: [], // Sorting state
        // onSortingChange: (updater) => {}, // Sorting change handler
        // enableSorting: true, // Enable sorting
        // enableMultiSort: false, // Enable multi-sort
        // enableSortingRemoval: true, // Enable sorting removal
        // sortDescFirst: false, // Sort descending first

        // ===== FILTERING STATE =====
        // columnFilters: [], // Column filters state
        // onColumnFiltersChange: (updater) => {}, // Column filters change handler
        // enableColumnFilters: true, // Enable column filters
        // filterFns: {}, // Filter functions
        // globalFilterFn: 'fuzzy', // Global filter function

        // ===== GROUPING STATE =====
        // grouping: [], // Grouping state
        // onGroupingChange: (updater) => {}, // Grouping change handler
        // enableGrouping: false, // Enable grouping
        // enableGroupingRemoval: true, // Enable grouping removal

        // ===== CUSTOM RENDERERS =====
        // renderTopToolbar: ({ table }) => null, // Custom top toolbar renderer
        // renderBottomToolbar: ({ table }) => null, // Custom bottom toolbar renderer
        // renderToolbarInternalActions: ({ table }) => null, // Custom toolbar internal actions
        // renderToolbarAlertBanner: ({ table }) => null, // Custom toolbar alert banner
        // renderEmptyRowsFallback: ({ table }) => null, // Custom empty rows fallback
        // renderDetailPanel: ({ row }) => null, // Custom detail panel renderer
        // renderRowActionMenuItems: ({ row, table }) => [], // Custom row action menu items
        // renderEditRowDialogContent: ({ table, row, internalEditComponents }) => null, // Custom edit row dialog content
        // renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => null, // Custom create row dialog content
        // renderDeleteRowDialogContent: ({ row, table }) => null, // Custom delete row dialog content

        // ===== LOCALIZATION =====
        // localization: {}, // Localization object
        // enableTableHead: true, // Enable table head
        // enableTableFooter: false, // Enable table footer
        // enableTableBody: true, // Enable table body

        // ===== PERFORMANCE =====
        // enableRowVirtualization: false, // Enable row virtualization
        // enableColumnVirtualization: false, // Enable column virtualization
        // virtualizerInstanceRef: null, // Virtualizer instance ref
        // virtualizerProps: {}, // Virtualizer props

        renderTopToolbarCustomActions: ({ table }) => (
            <></>
        ),

        // ===== ROW ACTIONS (Currently Active) =====
        enableRowActions: true,
        positionActionsColumn: 'last',
        renderRowActions: ({ row, table }) => (
            <Box sx={{ display: 'flex', gap: '1rem' }}>
                <Tooltip title="Edit">
                    <IconButton
                        sx={{ margin: 0, padding: 0 }}
                        onClick={() => onEdit(row?.original)}
                    >
                        <EditIcon sx={{ color: 'var(--template-palette-primary-main)', fontSize: '18px' }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                    <IconButton
                        sx={{ margin: 0, padding: 0 }}
                        onClick={() => onDelete(row?.original)}
                    >
                        <DeleteIcon sx={{ color: 'var(--template-palette-error-main)', fontSize: '18px' }} />
                    </IconButton>
                </Tooltip>
            </Box>
        ),
    });

    return(
        <MaterialReactTable table={table} />
    )
}
